import React from 'react';
import { useState } from 'react';
import { Piano, Hand, Award, Music2, Sparkles, Heart, X, Shield, CheckCircle, Clock, Phone, Mail, Volume2, Star } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const PianoAcoustiquesOccasion = () => {
  const scrollRef1 = useScrollAnimation();
  const scrollRef2 = useScrollAnimation();
  const scrollRef3 = useScrollAnimation();
  const [selectedPiano, setSelectedPiano] = useState<any>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  const pianos = [
    {
      brand: "Pleyel Droit 1960",
      image: "/image12.jpg",
      year: "1960",
      condition: "Restauré",
      features: ["Manufacture française", "Sonorité unique", "Restauration complète", "Pièce de collection"],
      specs: {
        hauteur: "118 cm",
        largeur: "145 cm",
        profondeur: "58 cm",
        poids: "180 kg",
        cordes: "220 cordes",
        marteaux: "Restaurés à neuf",
        finition: "Acajou restauré",
        origine: "Paris, France",
        restauration: "Complète - 2023"
      }
    },
    {
      brand: "Yamaha U1 1985",
      image: "https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg?auto=compress&cs=tinysrgb&w=800",
      year: "1985",
      condition: "Excellent état",
      features: ["121 cm", "Mécanique révisée", "Accord professionnel", "Fiabilité légendaire"],
      specs: {
        hauteur: "121 cm",
        largeur: "153 cm",
        profondeur: "61 cm",
        poids: "200 kg",
        cordes: "220 cordes",
        marteaux: "Révisés",
        finition: "Ébène satiné",
        origine: "Hamamatsu, Japon",
        revision: "Mécanique - 2024"
      }
    },
    {
      brand: "Steinway & Sons 1/4 Queue",
      image: "/imageskawai.jpeg",
      year: "1978",
      condition: "Restauré maître",
      features: ["170 cm", "Son légendaire", "Restauration d'art", "Investissement patrimonial"],
      specs: {
        longueur: "170 cm",
        largeur: "148 cm",
        hauteur: "102 cm",
        poids: "320 kg",
        cordes: "230 cordes",
        marteaux: "Renner Hamburg",
        finition: "Ébène poli brillant",
        origine: "New York, USA",
        restauration: "Maître artisan - 2022"
      }
    }
  ];

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div 
            ref={scrollRef1 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-light heading-modern mb-6">
              Pianos Acoustiques <span className="heading-accent">d'Occasion</span>
            </h1>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-white/70 font-light max-w-4xl mx-auto leading-relaxed">
              Instruments d'exception avec histoire et caractère. Chaque piano acoustique d'occasion 
              est restauré avec passion par nos maîtres artisans.
            </p>
          </div>

          {/* Avantages */}
          <div 
            ref={scrollRef2 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal grid md:grid-cols-4 gap-6 mb-20"
          >
            {[
              { icon: Volume2, title: "Bonne Acoustique", desc: "Son naturel authentique" },
              { icon: Music2, title: "Différente Sonorité", desc: "Variété de timbres" },
              { icon: Hand, title: "Précision du Toucher", desc: "Sensibilité exceptionnelle" },
              { icon: Clock, title: "Longevité", desc: "Durabilité de l'instrument" }
            ].map((feature, index) => (
              <div key={index} className="glass-card interactive-card rounded-2xl p-6 text-center light-effect">
                <feature.icon className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-white/60 font-light text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Collection */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef3 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal"
          >
            <h2 className="text-3xl font-light text-center text-white mb-16">
              Trésors <span className="heading-accent">Musicaux</span>
            </h2>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {pianos.map((piano, index) => (
                <div key={index} className="glass-card interactive-card rounded-2xl overflow-hidden light-effect">
                  <div className="relative h-64">
                    <img
                      src={piano.image}
                      alt={piano.brand}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                    <div className="absolute top-4 right-4">
                      <span className="bg-purple-400/20 backdrop-blur-sm text-purple-400 px-3 py-1 rounded-full text-sm font-semibold border border-purple-400/30">
                        {piano.year}
                      </span>
                    </div>
                    <div className="absolute top-4 left-4">
                      <span className="bg-amber-400/20 backdrop-blur-sm text-amber-400 px-3 py-1 rounded-full text-sm font-semibold border border-amber-400/30">
                        {piano.condition}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-semibold text-white">{piano.brand}</h3>
                      <span className="text-sm font-light text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-full border border-yellow-400/30">
                        Prix sur devis
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-6">
                      {piano.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-sm text-white/70">
                          <Star className="w-3 h-3 text-yellow-400 mr-2 flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Processus de restauration */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="glass-card rounded-3xl p-12 max-w-6xl mx-auto">
            <h3 className="text-3xl font-light text-center text-white mb-12">
              L'Art de la <span className="heading-accent">Restauration</span>
            </h3>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <img
                  src="https://images.pexels.com/photos/1407354/pexels-photo-1407354.jpeg"
                  alt="Restauration piano"
                  className="w-full h-64 object-cover rounded-2xl"
                />
                <div className="absolute top-4 right-4 bg-yellow-400 text-black font-bold text-2xl px-4 py-2 rounded-full z-10">
                  #15
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-yellow-400/20 rounded-full flex items-center justify-center flex-shrink-0 border border-yellow-400/30">
                    <span className="text-yellow-400 text-sm font-semibold">1</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Diagnostic Complet</h4>
                    <p className="text-white/60 font-light text-sm">Évaluation minutieuse de l'état général et du potentiel de l'instrument.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-yellow-400/20 rounded-full flex items-center justify-center flex-shrink-0 border border-yellow-400/30">
                    <span className="text-yellow-400 text-sm font-semibold">2</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Restauration Artisanale</h4>
                    <p className="text-white/60 font-light text-sm">Remise en état selon les techniques traditionnelles et les standards d'origine.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-yellow-400/20 rounded-full flex items-center justify-center flex-shrink-0 border border-yellow-400/30">
                    <span className="text-yellow-400 text-sm font-semibold">3</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Finition d'Excellence</h4>
                    <p className="text-white/60 font-light text-sm">Accord final et ajustements pour retrouver toute la splendeur originale.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal des détails */}
      {selectedPiano && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-semibold text-white">{selectedPiano.brand}</h3>
                <button 
                  onClick={() => setSelectedPiano(null)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <img 
                src={selectedPiano.image} 
                alt={selectedPiano.brand}
                className="w-full h-48 object-cover rounded-2xl mb-6"
              />
              
              <div className="flex gap-2 mb-4">
                <span className="bg-purple-400/20 backdrop-blur-sm text-purple-400 px-3 py-1 rounded-full text-sm font-semibold border border-purple-400/30">
                  {selectedPiano.year}
                </span>
                <span className="bg-amber-400/20 backdrop-blur-sm text-amber-400 px-3 py-1 rounded-full text-sm font-semibold border border-amber-400/30">
                  {selectedPiano.condition}
                </span>
              </div>
              
              <h4 className="text-lg font-semibold text-white mb-4">Caractéristiques Techniques</h4>
              
              <div className="space-y-4">
                {Object.entries(selectedPiano.specs).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-start py-2 border-b border-white/10">
                    <span className="text-white/70 font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                    <span className="text-white text-right max-w-xs">{value}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 flex gap-4">
                <button 
                  onClick={() => {
                    setSelectedPiano(null);
                    scrollToContact();
                  }}
                  className="btn-premium px-6 py-3 rounded-lg font-medium flex-1"
                >
                  Contactez-nous
                </button>
                <button 
                  onClick={() => setSelectedPiano(null)}
                  className="bg-white/5 hover:bg-white/10 border border-white/20 hover:border-yellow-400/30 text-white hover:text-yellow-400 px-6 py-3 rounded-lg font-light transition-all duration-300 flex-1"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PianoAcoustiquesOccasion;